import { BrowserRouter as Router, Switch, Route } from 'react-router-dom'
import { theme, ThemeProvider } from '@black-bear-energy/rucksack'
import '@black-bear-energy/rucksack/dist/rucksack.css'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { createMUITheme } from './utils/createMUITheme'
import './App.css'
import { useTimedNotifications } from './state/modules/notifications'
import ConfirmationModal from './components/ConfirmationModal'
import NotificationsToast from './components/NotificationsToast'
import Header from './containers/Header'
import Login from './containers/Login'
import {
  useAuthInfo,
  useGetIDToken,
  useImpersonatingUserOrUser,
  isBBEUserOrSuperAdmin,
} from './state/modules/auth'
import { useCallOnceOnAuthenticationVerified } from './state/modules/common'
import WithAuthenticationRequired from './state/modules/helpers/WithAuthenticationRequired'
import TOSProvider from './state/modules/helpers/TOSProvider'
import { usePreloadForBBEUsersOrSuperAdmins } from './state/modules/preloads'
import { Home } from './containers/Home'
import { ManageDocuments } from './containers/ManageDocuments'
import { DocumentExchange } from './containers/DocumentExchange'
import { ManageUsers } from './containers/ManageUsers'
import { UserImport } from './containers/UserImport'
import { BidHistory } from './containers/BidHistory'
import { SiteUpload } from './containers/SiteUpload'
import { SolarLeaderboardUpload } from './containers/SolarLeaderboardUpload'
import { enableBidHistory, enableSideNavigation } from './featureFlags'
import { Projects } from './containers/Projects'
import { Dashboard } from './containers/Dashboard'
import { Sites } from './containers/Sites'
import { SiteDetail } from './containers/SiteDetail'
import { AppLayout } from './components/Navigation/AppLayout'

const queryClient = new QueryClient()

function App() {
  const getIDToken = useGetIDToken()

  useCallOnceOnAuthenticationVerified(getIDToken)
  usePreloadForBBEUsersOrSuperAdmins()
  useTimedNotifications()

  const {
    isLoading,
    settings: { enableProjectsPage, enableDashboard, enableSitesPage },
  } = useAuthInfo()

  const impersonatingUserOrUser = useImpersonatingUserOrUser()

  const authenticatedRoutes = (
    <TOSProvider>
      <Route path="/">
        <Switch>
          <Route path="/rfps">
            <WithAuthenticationRequired Component={Home} />
          </Route>
          <Route path="/manage/users">
            <WithAuthenticationRequired Component={ManageUsers} />
          </Route>
          <Route path="/manage/documents">
            <WithAuthenticationRequired Component={ManageDocuments} />
          </Route>
          <Route path="/document-exchange">
            <WithAuthenticationRequired Component={DocumentExchange} />
          </Route>
          <Route path="/manage/projects">
            <WithAuthenticationRequired
              Component={Projects}
              loading={isLoading}
              redirectOn={() => !isLoading && !enableProjectsPage}
              redirectTo="/"
            />
          </Route>
          <Route path="/user-import">
            <WithAuthenticationRequired Component={UserImport} />
          </Route>
          {enableBidHistory && (
            <Route path="/bid/history">
              <WithAuthenticationRequired Component={BidHistory} />
            </Route>
          )}
          <Route path="/sites/upload">
            <WithAuthenticationRequired Component={SiteUpload} />
          </Route>
          {impersonatingUserOrUser &&
            isBBEUserOrSuperAdmin(impersonatingUserOrUser) && (
              <Route path="/leaderboard/upload">
                <WithAuthenticationRequired
                  Component={SolarLeaderboardUpload}
                />
              </Route>
            )}
          {enableSitesPage && (
            <Route exact path="/sites/:siteRFPId">
              <WithAuthenticationRequired Component={SiteDetail} />
            </Route>
          )}
          {enableSitesPage && (
            <Route path="/sites">
              <WithAuthenticationRequired Component={Sites} />
            </Route>
          )}
          <Route path="/">
            <WithAuthenticationRequired
              Component={Dashboard}
              loading={isLoading}
              redirectOn={() =>
                !(isLoading || enableDashboard || enableSideNavigation)
              }
              redirectTo="/rfps"
            />
          </Route>
        </Switch>
      </Route>
    </TOSProvider>
  )

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <MuiThemeProvider theme={createMUITheme()}>
          <Router>
            {!enableSideNavigation && <Header />}
            <ConfirmationModal />
            <NotificationsToast />
            <Switch>
              <Route path="/login">
                <Login />
              </Route>
              <Route>
                {enableSideNavigation ? (
                  <AppLayout>{authenticatedRoutes}</AppLayout>
                ) : (
                  authenticatedRoutes
                )}
              </Route>
            </Switch>
          </Router>
        </MuiThemeProvider>
      </ThemeProvider>
      <ReactQueryDevtools />
    </QueryClientProvider>
  )
}

export default App
